import type { User } from '@/store/auth'
import { useAuthStore } from '@/store/auth'
import { api } from './client'

export interface LoginRequest {
  email: string
  password: string
  twoFactorCode?: string
}

export interface RegisterRequest {
  email: string
  password: string
  name: string
  inviteCode?: string
  referralCode?: string
}

export interface AuthResponse {
  user: User
  accessToken: string
  refreshToken: string
  accessTokenExpiresAt?: number
}

export interface PasswordResetRequest {
  email: string
}

export interface PasswordResetConfirm {
  token: string
  password: string
}

// Base path for auth endpoints
const BASE_PATH = '/v1/auth'

export const authApi = {
  login: (data: LoginRequest) =>
    api.post<AuthResponse>(`${BASE_PATH}/login`, data, { skipAuth: true }),

  register: (data: RegisterRequest) =>
    api.post<AuthResponse>(`${BASE_PATH}/register`, data, { skipAuth: true }),

  logout: () =>
    api.post<void>(`${BASE_PATH}/logout`),

  me: () =>
    api.get<User>(`${BASE_PATH}/me`),

  refreshToken: (refreshToken: string) =>
    api.post<{ accessToken: string; refreshToken: string }>(
      `${BASE_PATH}/refresh`,
      { refreshToken },
      { skipAuth: true }
    ),

  requestPasswordReset: (data: PasswordResetRequest) =>
    api.post<void>(`${BASE_PATH}/password-reset`, data, { skipAuth: true }),

  confirmPasswordReset: (data: PasswordResetConfirm) =>
    api.post<void>(`${BASE_PATH}/password-reset/confirm`, data, { skipAuth: true }),

  verifyEmail: (token: string) =>
    api.post<{ success: boolean; user?: { id: string; email: string; name: string; plan: string; emailVerified: boolean; createdAt: string } }>(`${BASE_PATH}/verify-email`, { token }, { skipAuth: true }),

  resendVerification: () =>
    api.post<void>(`${BASE_PATH}/resend-verification`),

  /** GDPR Art. 20 — Export all user data as downloadable JSON */
  exportData: async (): Promise<Blob> => {
    const token = useAuthStore.getState().token
    const response = await fetch(`${BASE_PATH}/me/export`, {
      headers: {
        'Authorization': `Bearer ${token || ''}`,
      },
    })
    if (!response.ok) throw new Error('Failed to export data')
    return response.blob()
  },

  /** GDPR Art. 17 — Delete account (Right to Erasure) */
  deleteAccount: async (password: string): Promise<{ success: boolean; message: string }> => {
    const token = useAuthStore.getState().token
    const response = await fetch(`${BASE_PATH}/me/delete`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token || ''}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to delete account' }))
      throw new Error(err.error || err.message || 'Failed to delete account')
    }
    return response.json()
  },
}
